import { PageLink, PageTitle } from "_metronic/layout/core";
import { ExternalAccessData } from "app/modules/auth";
import { shallowEqual, useSelector } from "react-redux";
import { RootState } from "setup/redux/RootReducer";
import { CPLAnalytics } from "setup/appInsights/CPLAnalytics";
import { externalPasswordChange, selectActionSuccessful, selectLoading } from "../ExternalAccessSlice";
import { useThunkDispatch } from "setup/redux/thunkDispatch";
import { Form, Formik } from "formik";
import { passwordValidationRegex } from "cpl-react-common/components/Constants";
import * as Yup from "yup";
import { useEffect } from "react";
import { ExternalPasswordChangeModel } from "services/api/cplauthapi/CPLAuthAPI";
import { selectUpdatingPassword } from "app/modules/auth/resetPassword/ResetPasswordSlice";
import CPLPasswordInput from "cpl-react-common/components/Inputs/CPLPasswordInput";

const externalAccessPasswordChangeBreadCrumbs: Array<PageLink> = [
  {
    title: "Password",
    path: "/passwordchange",
    isSeparator: false,
    isActive: true,
  },
];

const passwordPasswordChangeSchema = Yup.object().shape({
  password: Yup.string()
    .required("Please enter your new password")
    .matches(
      passwordValidationRegex,
      "Password must be at least 8 characters in length and contain one number, one upper case letter and one lower case letter"
    ),
  repeatPassword: Yup.string()
    .required("Please re-enter your password")
    .oneOf([Yup.ref("password")], "Password does not match"),
});

const ExternalAccessPasswordChangePage = () => {
  const externalAccessData = useSelector<RootState, ExternalAccessData | undefined>(
    ({ auth }) => auth.externalAccess,
    shallowEqual
  );
  const loading = useSelector(selectLoading);
  const thunkDispatch = useThunkDispatch();

  const updatingPassword = useSelector(selectUpdatingPassword);
  const actionSuccessful = useSelector(selectActionSuccessful);

  useEffect(() => {
    if (actionSuccessful) {
      window.location.href = "/dashboard";
    }
  }, [actionSuccessful]);

  const initialValues = {
    password: "",
    repeatPassword: "",
  };

  const handleUpdatePassword = (newPassword: string) => {
    thunkDispatch(
      externalPasswordChange({
        passwordModel: new ExternalPasswordChangeModel({
          singleUseToken: externalAccessData?.singleUseToken,
          newPassword,
        }),
      })
    );
  };

  return (
    <CPLAnalytics pageName="ExternalAccessPasswordChange">
      <PageTitle breadcrumbs={externalAccessPasswordChangeBreadCrumbs} />
      <Formik
        enableReinitialize
        initialValues={initialValues}
        onSubmit={async (values) => {
          handleUpdatePassword(values.password);
        }}
        validationSchema={passwordPasswordChangeSchema}
      >
        {({ getFieldProps }) => (
          <Form>
            <div className="text-center mb-10">
              <h1 className="heading-text cpl-text-primary mb-3">Reset Password</h1>
            </div>
            <div className="fv-row mb-6">
              <label className="form-label fs-6 fw-bolder" htmlFor="password">
                Enter Password
              </label>
              <CPLPasswordInput
                className="form-control bg-transparent"
                id="password"
                getFieldProps={getFieldProps}
                data-test-id="external-reset-password-form__password-input"
              />
            </div>
            <div className="fv-row mb-10">
              <label className="form-label fs-6 fw-bolder" htmlFor="repeatPassword">
                Repeat Password
              </label>
              <CPLPasswordInput
                className="form-control bg-transparent"
                id="repeatPassword"
                getFieldProps={getFieldProps}
                data-test-id="external-reset-password-form__repeat-password-input"
              />
            </div>
            {/* begin::Action */}
            <div className="text-center">
              <button
                type="submit"
                id="kt_sign_in_submit"
                className="cpl-btn btn-lg cpl-btn-primary w-100 mb-5"
                data-test-id="external-reset-password-form__submit-button"
              >
                {loading || updatingPassword ? (
                  <span className="indicator-progress" style={{ display: "block" }}>
                    Please wait...
                    <span className="spinner-border spinner-border-sm align-middle ms-2"></span>
                  </span>
                ) : (
                  <span className="indicator-label">Save Changes</span>
                )}
              </button>
            </div>
          </Form>
        )}
      </Formik>
    </CPLAnalytics>
  );
};

export default ExternalAccessPasswordChangePage;
